import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { Grid, Button, RootRef, TextField, Avatar, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Person, Send, Close } from '@material-ui/icons';
import { I18nContext } from 'translations';
import { ChatFactory } from 'utils/chat/ChatFactory';
import BookingChatMessage from './BookingChatMessage';
import styles from './style';

function BookingChat({
  bookingId,
  driver,
  passenger,
  classes,
  openViewChat,
  disabledChat,
  sendAsShopper,
  deliveryRouteId,
  prePackageId,
  reference,
  customerName,
  deliveryAddress,
  companyName,
}) {
  const messagesChat = useRef();
  const [messages, setMessages] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [isUsingFirebaseFallback, setIsUsingFirebaseFallback] = useState(false);
  const [writingStatus, setWritingStatus] = useState({ isWriting: false, message: null });
  // Chat de ruta: el servidor rechazó la suscripción, cerró el hilo o falló un envío.
  const [routeChatRejected, setRouteChatRejected] = useState(false);
  const [threadClosed, setThreadClosed] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  // Extraer la versión del driver a una variable separada para ESLint
  const driverVersion = driver?.app_version;
  
  // Crear el chat adapter basado en la versión del driver
  // Si driver.app_version >= 5.40.22 usa gRPC, sino usa Firebase
  // Con deliveryRouteId y prePackageId es el chat entre el conductor de la ruta y la
  // compañía dueña de ESE prepaquete (specs/007-chat-por-paquete/): un hilo por paquete,
  // no por compañía.
  const isRouteChat = Boolean(deliveryRouteId && prePackageId);
  const chat = useMemo(() => {
    return ChatFactory.createChatAdapter(
      isRouteChat ? { deliveryRouteId, prePackageId } : driverVersion
    );
  }, [driverVersion, deliveryRouteId, prePackageId]); //eslint-disable-line
  const { translate } = useContext(I18nContext);

  const getMessages = () => {
    chat.listenForMessages(bookingId, (newMessage) => {
      setMessages(prevState => {
        const updatedMessages = [...prevState, newMessage];

        // Enviar read receipt con el ID del último mensaje (el que acabamos de recibir)
        if (chat && chat.sendReadReceipt && newMessage.id) {
          chat.sendReadReceipt(bookingId, newMessage.id);
        }

        return updatedMessages;
      });
    });
    messagesChat.current.scrollTop = messagesChat.current.scrollHeight;
  }

  useEffect(() => {
    if (chat.setHandlers) {
      chat.setHandlers({
        onRejected: () => setRouteChatRejected(true),
        onClosed: () => setThreadClosed(true),
        onError: () => setSendFailed(true),
      });
    }
    getMessages();

    // Enviar read receipt cuando el usuario abre el chat
    // Esperar a que se carguen los mensajes para obtener el ID del último
    if (chat && chat.sendReadReceipt) {
      // Esperar un poco más para asegurar que los mensajes se hayan cargado
      setTimeout(() => {
        // Obtener el último mensaje del estado
        setMessages(currentMessages => {
          if (currentMessages.length > 0) {
            const lastMessage = currentMessages[currentMessages.length - 1];
            if (lastMessage.id) {
              chat.sendReadReceipt(bookingId, lastMessage.id);
            }
          }
          return currentMessages;
        });
      }, 1000);
    }

    // Escuchar estado de "escribiendo..." del otro usuario (solo para gRPC)
    if (chat && chat.listenForWriting) {
      chat.listenForWriting(bookingId, (status) => {
        // Obtener el ID del passenger (usuario actual en la web)
        const currentUserId = passenger?.id || passenger?._id;
        const writingUserId = status.userId;

        // Convertir ambos a string para comparación segura
        const currentUserIdStr = currentUserId ? String(currentUserId) : null;
        const writingUserIdStr = writingUserId ? String(writingUserId) : null;

        // Si el userId que está escribiendo es el mismo que el passenger (usuario actual), no mostrar
        if (currentUserIdStr && writingUserIdStr && currentUserIdStr === writingUserIdStr) {
          setWritingStatus({ isWriting: false, message: null });
        } else {
          setWritingStatus(status);
        }
      });
    }

    // Verificar periódicamente si está usando Firebase fallback
    const checkFallback = setInterval(() => {
      if (chat && chat.isUsingFirebaseFallback) {
        const usingFallback = chat.isUsingFirebaseFallback(bookingId);
        setIsUsingFirebaseFallback(usingFallback);
      }
    }, 500);

    // Cleanup: desconectar el chat cuando el componente se desmonte
    return () => {
      clearInterval(checkFallback);
      if (chat && chat.disconnect) {
        chat.disconnect(bookingId);
      }
    };
  }, []) // eslint-disable-line

  useEffect(() => {
    messagesChat.current.scrollTop = messagesChat.current.scrollHeight;
  }, [messages])

  const handleSubmit = e => {
    e.preventDefault();
    if (chatMessage && chatMessage.trim() !== '') {
      setSendFailed(false);
      if (isRouteChat) {
        // El lado (compañía / picap) lo decide el servidor según la sesión.
        chat.sendMessage(chatMessage.trim(), null, bookingId);
      } else if (sendAsShopper) {
        chat.sendMessage(chatMessage.trim(), chat.AS_SHOPPER, bookingId);
      } else {
        chat.sendMessage(chatMessage.trim(), chat.AS_PICAP, bookingId);
      }
      setChatMessage('');
    } else {
      setChatMessage('');
    }
  }

  const handleInputChange = e => {
    setChatMessage(e.target.value);

    // Activar indicador de "escribiendo..." cuando el usuario escribe (solo para gRPC)
    if (e.target.value && e.target.value.trim() !== '') {
      if (chat && chat.startWriting) {
        chat.startWriting(bookingId);
      }
    } else {
      // Si borra todo el texto, detener el indicador
      if (chat && chat.stopWriting) {
        chat.stopWriting(bookingId);
      }
    }
  };

  // `disabledChat` llega con la lógica invertida: truthy significa chat habilitado.
  const inputDisabled = !disabledChat || routeChatRejected || threadClosed;

  return (
    <div className={classes.boxChat}>
      <div className={classes.header}>
        <Grid container spacing={2} alignItems="center">
          <>
            <Grid item sm={3} xs={4} className={classes.textCenter}>
              {driver
                ? <Avatar
                    alt="Driver"
                    src={driver.photo_url}
                    className={classes.avatar}
                  />
                : <Avatar className={classes.avatar}>
                    <Person />
                  </Avatar>
              }
            </Grid>
            <Grid item sm={9} xs={8} className={classes.headerText}>
              {isRouteChat ? (
                <>
                  <Typography variant="subtitle2" color="textPrimary" className={classes.headerTitle}>
                    {reference ? `#${reference}` : (driver && driver.name)}
                  </Typography>
                  {(customerName || deliveryAddress) && (
                    <Typography variant="caption" color="textSecondary" display="block" className={classes.headerMeta} title={[customerName, deliveryAddress].filter(Boolean).join(' · ')}>
                      {[customerName, deliveryAddress].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {companyName && (
                    <Typography variant="caption" color="textSecondary" display="block" className={classes.headerMeta}>
                      {companyName}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="subtitle2" color="textPrimary">
                  {driver && driver.name}
                </Typography>
              )}
              <Button
                color="primary"
                className={classes.btnClose}
                onClick={openViewChat}
              >
                <Close />
              </Button>
            </Grid>
          </>
        </Grid>
      </div>
      <RootRef rootRef={messagesChat}>
        <Grid container className={classes.containerMessages}>
          {messages.map((message, index) => (
            <BookingChatMessage
              message={message}
              key={index.valueOf()}
              isGrpc={!isUsingFirebaseFallback}
            />
          ))}
        </Grid>
      </RootRef>
      {(routeChatRejected || threadClosed || sendFailed) && (
        <Typography variant="caption" color="error" style={{ padding: '4px 16px' }}>
          {translate(
            routeChatRejected
              ? 'app.booking.chat.routeChatUnavailable'
              : threadClosed
                ? 'app.booking.chat.routeChatClosed'
                : 'app.booking.chat.sendFailed'
          )}
        </Typography>
      )}
      <form className={classes.form} onSubmit={handleSubmit}>
        {writingStatus.isWriting && (
          // Dentro del form: está anclado abajo (position: absolute) y crece hacia arriba, así que el
          // aviso queda siempre visible sobre el campo de texto y no tapado por él.
          <div className={classes.writingIndicatorMessage}>
            <div
              className={classes.writingIndicatorBubble}
              role="status"
              data-testid="typing-indicator"
              aria-label={writingStatus.message || translate('app.booking.chat.writing')}
            >
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        )}

        <Grid container style={{ padding: '1px 10px 10px 10px' }}>
          <Grid item xs={10}>
            <TextField
              value={chatMessage}
              className={classes.inputSubmit}
              placeholder={translate('app.booking.chat.writeAMessage')}
              onChange={handleInputChange}
              disabled={inputDisabled}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              color="primary"
              type="submit"
              className={classes.btnSubmit}
              disabled={inputDisabled}
            >
              <Send />
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}

export default withStyles(styles)(BookingChat);